import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Grid, Box, TextField, MenuItem, Typography, IconButton, InputAdornment } from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { formatBranchDisplay } from "../../../../utils/formatBranchDisplay";
import { getPrimarySpecialtyId, getTaxonomyEntityName } from "../../../../utils/taxonomyFields";
import { appColors } from "../../../../constants/theme";
import {
    getSpecialtyList,
    getSpecialtyTitleMap,
    getTitleList,
    getTitlesFollowSpecialty,
} from "../professionPayloadUtils";
import { stepTitleSx } from "../style";

const RegisterStepTwo = forwardRef(({ onSuccess, onLoadingChange, onBranchIdChange, initialValues, step, lockBranchAndTitle = false, hideSpecialty = false }, ref) => {
    const { t } = useTranslation();
    const profile = useSelector((state) => state.doctor.profile);
    const formData = initialValues || profile;
    const selectedProfession = useSelector((state) => state.profession.selected);
    const prevSpecialtyIdRef = useRef(getPrimarySpecialtyId(formData) || "");

    const inputFieldStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: appColors.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: appColors.primary,
            },
        },
        '& .MuiInputLabel-root': {
            color: 'rgba(0, 0, 0, 0.6)',
            '&.Mui-focused': {
                color: appColors.primary,
            },
        },
    };

    const titleList = getTitleList(selectedProfession);
    const branchList = getSpecialtyList(selectedProfession);
    const specialtyTitleMap = getSpecialtyTitleMap(selectedProfession);
    const titlesFollowSpecialty = getTitlesFollowSpecialty(selectedProfession);
    const hideTitlePicker = titlesFollowSpecialty;

    const resolveMappedTitle = (specialtyId) => {
        if (!specialtyTitleMap || !specialtyId) return "";
        return (
            specialtyTitleMap[specialtyId] ||
            specialtyTitleMap[specialtyId.toLowerCase()] ||
            ""
        );
    };

    const formik = useFormik({
        initialValues: {
            about: formData?.about || "",
            titleId: formData?.titleId || "",
            primarySpecialtyId: getPrimarySpecialtyId(formData) || "",
            experienceYear: formData?.experienceYear ? String(Math.min(50, formData.experienceYear)) : "1",
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            titleId: hideTitlePicker
                ? Yup.string().nullable()
                : Yup.string().required("Ünvan seçiniz"),
            primarySpecialtyId: Yup.string().required("Uzmanlık seçiniz"),
            experienceYear: Yup.string()
                .required("Kaç yıl tecrübeniz var?")
                .matches(/^[0-9]+$/, "Lütfen yıl sayısını rakam olarak giriniz")
                .test('min-1', 'En az 1 yıl giriniz', (v) => {
                    if (!v) return false;
                    const n = parseInt(v, 10);
                    return !Number.isNaN(n) && n >= 1;
                })
                .test('max-50', 'En fazla 50 yıl giriniz', (v) => {
                    if (!v) return false;
                    const n = parseInt(v, 10);
                    return !Number.isNaN(n) && n <= 50;
                }),
        }),
        onSubmit: async (values) => {
            try {
                if (onLoadingChange) onLoadingChange(true);
                let titleId = values.titleId;
                if (hideTitlePicker && values.primarySpecialtyId) {
                    titleId = resolveMappedTitle(values.primarySpecialtyId) || titleId;
                }
                if (onSuccess) {
                    onSuccess({ ...values, titleId });
                }
            } finally {
                if (onLoadingChange) onLoadingChange(false);
            }
        },
    });

    React.useEffect(() => {
        const currentData = initialValues || profile;
        if (currentData) {
            const currentValues = formik.values;
            const nextSpecialtyId =
                getPrimarySpecialtyId(currentData) || currentValues.primarySpecialtyId || "";
            let nextTitleId = currentData.titleId || currentValues.titleId || "";
            if (hideTitlePicker && nextSpecialtyId) {
                const mapped = resolveMappedTitle(nextSpecialtyId);
                if (mapped) nextTitleId = mapped;
            }
            formik.setValues({
                about: currentData.about || currentValues.about || "",
                titleId: nextTitleId,
                primarySpecialtyId: nextSpecialtyId,
                experienceYear: currentData.experienceYear ? String(Math.min(50, currentData.experienceYear)) : (currentValues.experienceYear || "1")
            }, false);
            prevSpecialtyIdRef.current = nextSpecialtyId;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues, profile, hideTitlePicker]);

    useImperativeHandle(ref, () => ({
        handleSubmit: async () => {
            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                formik.setTouched({
                    titleId: true,
                    primarySpecialtyId: true,
                    experienceYear: true,
                });
                const firstErrorKey = Object.keys(errors)[0];
                const el = document.querySelector(`[name="${firstErrorKey}"]`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    if (typeof el.focus === 'function') el.focus();
                }
                return;
            }
            formik.handleSubmit();
        }
    }));

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Typography variant="h6" sx={stepTitleSx}>
                    Uzmanlık Bilgileri
                </Typography>

                <Grid container spacing={3} sx={{ width: '100%' }}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            variant="outlined"
                            id="about"
                            name="about"
                            label={t("about")}
                            placeholder={t("about_placeholder")}
                            value={formik.values.about}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            sx={inputFieldStyle}
                        />
                    </Grid>
                    {!hideTitlePicker && (
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                select
                                disabled={lockBranchAndTitle}
                                id="titleId"
                                name="titleId"
                                label={t("title")}
                                placeholder={t("title_select_placeholder")}
                                value={formik.values.titleId}
                                onChange={lockBranchAndTitle ? undefined : formik.handleChange}
                                helperText={
                                    lockBranchAndTitle
                                        ? t("doctor_profile_taxonomy_locked")
                                        : formik.touched.titleId && formik.errors.titleId
                                }
                                onBlur={(e) => {
                                    formik.handleBlur(e);
                                    const v = (formik.values.experienceYear || '').trim();
                                    if (v === '') {
                                        formik.setFieldValue('experienceYear', '1');
                                    }
                                }}
                                error={!lockBranchAndTitle && formik.touched.titleId && Boolean(formik.errors.titleId)}
                                sx={inputFieldStyle}
                            >
                                <MenuItem value="">{t("title_select_placeholder")}</MenuItem>
                                {titleList.map((title) => (
                                    <MenuItem key={title.id} value={title.id}>
                                        {title.titleName ?? title.TitleName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    )}

                    {!hideSpecialty && (
                    <Grid size={{ xs: 12 }}>
                            <TextField
                            fullWidth
                            variant="outlined"
                            select
                            disabled={lockBranchAndTitle}
                            id="primarySpecialtyId"
                            name="primarySpecialtyId"
                            label={t("branch")}
                            placeholder={t("branch_select_placeholder")}
                            value={formik.values.primarySpecialtyId}
                            helperText={
                                lockBranchAndTitle
                                    ? t("doctor_profile_taxonomy_locked")
                                    : formik.touched.primarySpecialtyId && formik.errors.primarySpecialtyId
                            }
                            onChange={(e) => {
                                if (lockBranchAndTitle) return;
                                formik.handleChange(e);
                                const newId = e.target.value;
                                if (hideTitlePicker && newId) {
                                    const mappedTitle = resolveMappedTitle(newId);
                                    if (mappedTitle) formik.setFieldValue("titleId", mappedTitle);
                                }
                                if (onBranchIdChange && newId && newId !== prevSpecialtyIdRef.current) {
                                    prevSpecialtyIdRef.current = newId;
                                    onBranchIdChange(newId);
                                } else {
                                    prevSpecialtyIdRef.current = newId;
                                }
                            }}
                            onBlur={formik.handleBlur}
                            error={!lockBranchAndTitle && formik.touched.primarySpecialtyId && Boolean(formik.errors.primarySpecialtyId)}
                            sx={inputFieldStyle}
                        >
                            <MenuItem value="">{t("branch_select_placeholder")}</MenuItem>
                            {branchList.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {formatBranchDisplay(getTaxonomyEntityName(branch))}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    )}

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            id="experienceYear"
                            name="experienceYear"
                            label={t("experience_year")}
                            placeholder={t("experience_year_placeholder_input")}
                            value={formik.values.experienceYear || ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (v === '' || /^[0-9]+$/.test(v)) {
                                    if (v === '') {
                                        formik.setFieldValue('experienceYear', '1');
                                    } else {
                                        const n = parseInt(v, 10);
                                        formik.setFieldValue('experienceYear', String(Math.max(1, Math.min(50, n))));
                                    }
                                }
                            }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.experienceYear && Boolean(formik.errors.experienceYear)}
                            helperText={formik.touched.experienceYear && formik.errors.experienceYear}
                            sx={inputFieldStyle}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', style: { textAlign: 'center' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            aria-label="decrement"
                                            size="small"
                                            onClick={() => {
                                                const current = parseInt(formik.values.experienceYear || '1', 10);
                                                const next = Math.max(1, (Number.isNaN(current) ? 1 : current) - 1);
                                                formik.setFieldValue('experienceYear', String(next));
                                            }}
                                        >
                                            <Remove fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="increment"
                                            size="small"
                                            onClick={() => {
                                                const current = parseInt(formik.values.experienceYear || '1', 10);
                                                const next = Math.min(50, (Number.isNaN(current) ? 1 : current) + 1);
                                                formik.setFieldValue('experienceYear', String(next));
                                            }}
                                        >
                                            <Add fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
});

export default RegisterStepTwo;
